/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, Leaf } from 'lucide-react';
import PostureBadge from '@/components/sustainability/PostureBadge';
import SignalGrid from '@/components/sustainability/SignalGrid';
import ReflectionNoteInput from '@/components/sustainability/ReflectionNoteInput';
import RecoveryIntegrityCard from '@/components/sustainability/RecoveryIntegrityCard';
import RecoveryWindowAwareness from '@/components/sustainability/RecoveryWindowAwareness';

/**
 * MySustainability — the counselor's OWN reflective view.
 *
 * STRICT: never shows peer signals, never leads with a score.
 * Recovery-oriented reflections come first; the 7-signal grid + Recovery
 * Integrity + Recovery Window Awareness + private Reflection Notes™ live
 * below for those who want detail.
 */
export default function MySustainabilityPage() {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [currentWeek, setCurrentWeek] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/sustainability/me`),
      axios.get(`${API}/sustainability/notes`),
    ])
      .then(([r, n]) => {
        setData(r.data);
        setNotes(n.data?.notes || []);
        setCurrentWeek(n.data?.current_week_iso || '');
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const noteFor = (signalKey) => notes.find(
    n => n.signal_key === signalKey && n.week_iso === currentWeek
  )?.note || '';

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="my-sustainability-page" className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10">
          <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239] flex items-center gap-1.5">
            <Leaf className="w-3 h-3" /> Sustainability · Self-reflection
          </p>
          <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-2">
            How is your week resting?
          </h1>
          <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-2 max-w-2xl leading-relaxed">
            A quiet, private view of your recent rhythm. Only you see this.
            Nothing here is judgement — these are reflective signals to help
            you protect your reflective space and your depth of presence.
          </p>
        </header>

        {loading ? (
          <Skeleton />
        ) : !data ? (
          <Skeleton message="Could not load your reflection right now." />
        ) : (
          <>
            <Reflections data={data} />
            <GeneralNote currentWeek={currentWeek} initial={noteFor('_general')} />
            <DetailToggle open={showDetail} onClick={() => setShowDetail(o => !o)} />
            {showDetail && (
              <Detail data={data} currentWeek={currentWeek} noteFor={noteFor} />
            )}
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function Skeleton({ message }) {
  return (
    <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
      {message ? message : <><Loader2 className="w-5 h-5 mx-auto animate-spin" /> Composing reflection…</>}
    </div>
  );
}

function Reflections({ data }) {
  const reflections = data.reflections || [];
  return (
    <section data-testid="my-sustainability-reflections" className="space-y-4">
      {reflections.length === 0 ? (
        <article className="rounded-2xl border border-[#EBE8E3] bg-white p-6 text-[12px] text-[#8A8681]">
          A calm week. Nothing surfaces — your rhythm is intact.
        </article>
      ) : (
        reflections.map((r, i) => (
          <article key={i} data-testid={`reflection-${i}`}
                    className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1]/30 p-5">
            <p className="text-[14px] text-[#5C3D1F] font-['Work_Sans'] leading-relaxed">{r}</p>
          </article>
        ))
      )}
    </section>
  );
}

function GeneralNote({ currentWeek, initial }) {
  if (!currentWeek) return null;
  return (
    <section data-testid="general-reflection-note"
              className="mt-6 rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <ReflectionNoteInput signalKey="_general" weekIso={currentWeek} initial={initial} />
    </section>
  );
}

function DetailToggle({ open, onClick }) {
  return (
    <button data-testid="my-sustainability-detail-toggle"
            onClick={onClick}
            className="mt-8 mb-2 text-[10px] uppercase tracking-[0.18em] text-[#8A8681] hover:text-[#5C5955] transition-colors">
      {open ? '— hide signal detail —' : '+ show signal detail (private)'}
    </button>
  );
}

function Detail({ data, currentWeek, noteFor }) {
  return (
    <section data-testid="my-sustainability-detail"
             className="rounded-2xl border border-[#EBE8E3] bg-white p-5 mt-2 space-y-4">
      <header className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">Sustainability posture</p>
        <PostureBadge posture={data.posture} />
      </header>
      <SignalGrid signals={data.signals} raw={data.raw_inputs} />

      {/* Recovery Integrity Signal™ */}
      <RecoveryIntegrityCard integrity={data.recovery_integrity} />

      {/* Recovery Window Awareness™ */}
      <RecoveryWindowAwareness awareness={data.recovery_window_awareness} />

      {/* Per-signal reflection notes — sacred private space */}
      {currentWeek && (
        <details data-testid="per-signal-notes" className="mt-2">
          <summary className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239] cursor-pointer">
            + per-signal reflection notes (private)
          </summary>
          <div className="mt-3 space-y-3">
            {Object.keys(data.signals).map(k => (
              <ReflectionNoteInput key={k}
                                    signalKey={k}
                                    weekIso={currentWeek}
                                    initial={noteFor(k)} />
            ))}
          </div>
        </details>
      )}

      <p className="text-[10px] text-[#8A8681] italic mt-2 leading-relaxed">
        These signals and notes are private to you. They support reflective awareness — never evaluation.
      </p>
    </section>
  );
}
